import { Grid, Stack } from '@mui/material'
import { CargoType, CarrierType, CarrierVisitStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { IssueDashboardCard } from '@planning/components/IssueDashboardCard'
import { ExtendedViewSwitch } from '@planning/components/visits-dashboard/ExtendedViewSwitch'
import { InboundDashboardCard } from '@planning/components/visits-dashboard/InboundDashboardCard'
import { OutboundDashboardCard } from '@planning/components/visits-dashboard/OutboundDashboardCard'
import { RestowDetailsDashboardCard } from '@planning/components/visits-dashboard/RestowDetailsDashboardCard'
import { VesselNotifications } from '@planning/components/visits-dashboard/VesselNotifications'
import { BaplieParseResult } from '@planning/pages/Order/stores/OrderListUploadParsingStore'
import { VesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, withAuthenticationRequired } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { vesselVisitDetailPages } from './Components/CarrierVisitDashboard'
import { VesselVisitDashboardHeader } from './Components/organisms/VesselVisitDashboardHeader/VesselVisitDashboardHeader'
import { VesselVisitOverview } from './Components/organisms/VesselVisitOverview/VesselVisitOverview'
import { OrderListUploadDialogV2 } from './Components/Upload/OrderListUploadDialog'
import { CarrierVisitDirection } from './VesselVisitDetails'

export interface VesselVisitDashboardExternalDeps {
  parseBaplie?: (file: File) => Promise<BaplieParseResult>
  unresolvedIssueCount?: number
  skipCraneSplit?: boolean
  stoppagesCount?: number
  hatchCoverSummary?: ReactNode
  stoppagesSummary?: ReactNode
  craneSplitCard?: ReactNode
  equipmentPlanningCard?: ReactNode
  kpiSummary?: ReactNode
}

type Props = {
  visitId: number
  vesselVisitItem: VesselVisitItem
  handlePageChange: (page: string) => void
  onClickExportOrders?: (direction: CarrierVisitDirection) => void
} & VesselVisitDashboardExternalDeps

export const VesselVisitDashboard: FC<Props> = withAuthenticationRequired(
  observer(
    ({
      vesselVisitItem,
      handlePageChange,
      visitId,
      onClickExportOrders,
      parseBaplie,
      unresolvedIssueCount = 0,
      skipCraneSplit,
      stoppagesCount = 0,
      hatchCoverSummary,
      stoppagesSummary,
      craneSplitCard,
      equipmentPlanningCard,
      kpiSummary,
    }) => {
      const theme = useMinimalsTheme()
      const navigate = useNavigate()

      const [isMinimalisticView, setIsMinimalisticView] = useState(false)
      const { orderListUploadViewStoreV2 } = usePlanningStore()

      if (!vesselVisitItem) return <></>

      const stoppageGridSize = useMemo(() => {
        let size = 4
        if (stoppagesCount) size = stoppagesCount * 4
        return size > 12 ? 12 : size
      }, [stoppagesCount])

      const isContainerType =
        vesselVisitItem?.data.cargoType &&
        vesselVisitItem?.data.cargoType !== CargoType.GeneralCargo

      return (
        <>
          <VesselVisitDashboardHeader vesselVisit={vesselVisitItem} />
          <Grid
            sx={{ padding: theme.spacing(3) }}
            container
            justifyContent='flex-start'
            spacing={{ xs: 1, md: 3 }}
          >
            <Grid size={12}>
              <VesselVisitOverview vesselVisitItem={vesselVisitItem} />
            </Grid>

            {isContainerType && (
              <Grid size={12}>
                <Stack alignItems='end'>
                  <ExtendedViewSwitch value={isMinimalisticView} onChange={setIsMinimalisticView} />
                </Stack>
              </Grid>
            )}

            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <InboundDashboardCard
                visitId={vesselVisitItem?.data.id}
                orders={vesselVisitItem?.discharge.orders ?? []}
                visitType={CarrierType.Vessel}
                visitStatus={vesselVisitItem?.data.status}
                cargoType={vesselVisitItem?.data.cargoType}
                isMinimalisticView={!isMinimalisticView}
                onViewClick={() => handlePageChange(vesselVisitDetailPages.discharge)}
                onClickExportOrders={onClickExportOrders}
                cardFooter={
                  <VesselNotifications orders={vesselVisitItem?.discharge.orders ?? []} />
                }
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <OutboundDashboardCard
                visitId={vesselVisitItem?.data.id}
                orders={vesselVisitItem?.load.orders ?? []}
                visitType={CarrierType.Vessel}
                visitStatus={vesselVisitItem?.data.status}
                cargoType={vesselVisitItem?.data.cargoType}
                isMinimalisticView={!isMinimalisticView}
                onViewClick={() => handlePageChange(vesselVisitDetailPages.load)}
                onClickExportOrders={onClickExportOrders}
                cardFooter={<VesselNotifications orders={vesselVisitItem?.load.orders ?? []} />}
              />
            </Grid>

            {isContainerType && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 4,
                }}
              >
                <RestowDetailsDashboardCard
                  visitId={vesselVisitItem?.data.id}
                  restows={vesselVisitItem?.restow?.dischargeOrders ?? []}
                  visitType={CarrierType.Vessel}
                  visitStatus={vesselVisitItem?.data.status}
                  cargoType={vesselVisitItem?.data.cargoType}
                  isMinimalisticView={!isMinimalisticView}
                  onViewClick={() => handlePageChange(vesselVisitDetailPages.restow)}
                />
              </Grid>
            )}

            <OrderListUploadDialogV2
              enableFieldSelectionFromOrdersView={false}
              open={orderListUploadViewStoreV2.open}
              handleDialogClose={() => {
                orderListUploadViewStoreV2.setOpen(false)
              }}
              visitType={CarrierType.Vessel}
              parseBaplie={parseBaplie}
            />

            <Grid
              size={{
                xs: 12,
                md: 6,
                lg: 4,
              }}
            >
              <IssueDashboardCard
                visitType={CarrierType.Vessel}
                vesselVisitItem={vesselVisitItem}
                unresolvedOperationsIssueCount={unresolvedIssueCount}
                onViewClick={() => handlePageChange(`${PATHS.issues.url}`)}
              />
            </Grid>
            {hatchCoverSummary && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                  lg: 4,
                }}
              >
                {hatchCoverSummary}
              </Grid>
            )}
            {isContainerType && !skipCraneSplit && (
              <>
                {craneSplitCard && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 4,
                    }}
                  >
                    {craneSplitCard}
                  </Grid>
                )}
                {equipmentPlanningCard && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                      lg: 4,
                    }}
                  >
                    {equipmentPlanningCard}
                  </Grid>
                )}
              </>
            )}
            {stoppagesSummary && (
              <Grid
                size={{
                  xs: 12,
                  lg: stoppageGridSize,
                }}
              >
                {stoppagesSummary}
              </Grid>
            )}
            {isContainerType && kpiSummary && (
              <Grid
                size={{
                  xs: 12,
                }}
              >
                {kpiSummary}
              </Grid>
            )}
          </Grid>
        </>
      )
    },
  ),
)
